/**
 * RWBR — EZRA PIT STOP TROUBLE (EZRA-S2 §3.9, Appendix B2).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  C9 IS RESOLVED. IT WAS NEVER A CONTRADICTION INSIDE SEASON 2.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `CROSS_REFERENCE.md` note C9 recorded a conflict: EZRA-S2 prose says *"Roll
 * 2D6"* while "the chart" is headed **2d8 Add** with rows running to 16, which
 * would make rows 13-16 unreachable. That was comparing S2's prose against the
 * EZRA **Track Pack's** chart, because S2's own appendix had never been read —
 * it is an embedded EMF image (`word/media/image11.emf`), invisible to every
 * text extraction of the docx.
 *
 * Appendix B2 has now been rendered and read. Its printed header is:
 *
 *     2D6 add     |     Pit Trouble Description
 *
 * with **eleven rows, 2 through 12**. It agrees with the S2 prose exactly.
 * There is no internal contradiction and nothing for Howard to rule on: the
 * 2d8/rows-to-16 version is the Track Pack's, and D-EZ-7 already subordinates
 * the Track Pack to the Season 2 appendices, because §3.9 points at its own
 * appendices by name — *"See Appendix B2 for results"*.
 *
 * The two charts are different documents, not two readings of one. Beyond the
 * dice, B2 has **no Lead Group / Middle Group split** (the Track Pack has two
 * result columns), and it drops the Track Pack's pit-road crash, defective
 * battery, jack failure and runaway tire entirely. The Track Pack's crewman
 * result *"permanently lowers pit crew rating for the rest of the race"*; B2's
 * is harsher and simpler — every remaining Fast Pit check in the race fails.
 * None of the Track Pack's rows were used here.
 *
 * ── WHAT TRIGGERS IT ───────────────────────────────────────────────────────
 *
 * EZRA-S2 §3.9 Pit Stop Rules, verbatim:
 *
 *   "When checking for a Fast Pit if the resulting dice roll is 12, the Pit Crew
 *    experiences TROUBLE in the pit. Roll 2D6 and read the results on the PIT
 *    STOP TROUBLE CHART"
 *
 * So the trigger is a FAST PIT roll of 12 — the metallic 2d6 the driver rolls
 * against his Fast Pit rating, on a pit turn. EZRA's maximum Fast Pit rating is
 * 9, so 12 can never also be a success: it is a clean sentinel.
 *
 * Note this is NOT the EZRA Track Pack's rule. The Track Pack says *"We also use
 * result number 11 to indicate a Pit Violation & 12 for designating Pit
 * TROUBLE"*. S2 keeps the 12, and says nothing about 11 — its pit violations
 * come from a different trigger entirely. See `pitTurn.ts`.
 *
 * ── DEFAULT DISPLACEMENT ───────────────────────────────────────────────────
 *
 * Printed under the title, verbatim:
 *
 *   "(Unless otherwise noted in the chart drivers are displaced to the Last
 *    Position in the Current Group)"
 *
 * That is what the bracketed [LPCG] on eight of the eleven rows abbreviates.
 * The source's brace typos are preserved in the row text: row 8 prints "{LPCG]"
 * and row 10 prints "[LPCG}".
 */

import type { Dice } from './dice.ts';
import type { Group } from './types.ts';

export const CITE_PIT_TROUBLE =
  'EZRA-S2 §3.9 / Appendix B2 — "PIT STOP TROUBLE CHART", headed "2D6 add", rows 2-12. Embedded in ' +
  'EZRAS2RulesFinal.docx as an EMF image (word/media/image11.emf), which is why no text extraction ' +
  'of that file ever showed it.';

export const CITE_PIT_TROUBLE_TRIGGER =
  'EZRA-S2 §3.9 — "When checking for a Fast Pit if the resulting dice roll is 12, the Pit Crew ' +
  'experiences TROUBLE in the pit. Roll 2D6 and read the results on the PIT STOP TROUBLE CHART".';

export const CITE_LPCG =
  'EZRA-S2 Appendix B2 header — "(Unless otherwise noted in the chart drivers are displaced to the ' +
  'Last Position in the Current Group)".';

export const CITE_C9_RESOLVED =
  'C9 resolved: Appendix B2 is printed "2D6 add" with rows 2-12, matching the S2 prose "Roll 2D6". ' +
  'The "2d8 Add" / rows-to-16 chart is the EZRA Track Pack\'s, which D-EZ-7 subordinates to the ' +
  'Season 2 appendices. No ruling is required.';

/** C9 no longer needs a ruling from Howard. Kept exported so the docs can assert it. */
export const C9_NEEDS_RULING = false;

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/** What the chart does to the car, as a closed set. */
export type PitTroublePlacement =
  /** [LPCG] — "Last Position in the Current Group". The chart's default. */
  | 'last-in-current-group'
  /** Row 11 — "Driver returns to field as the last driver on the lead lap." */
  | 'last-on-lead-lap'
  /** Row 12 — resolved by a d6; see `PitTroubleResult.fireRoll`. */
  | 'fire';

export interface PitTroubleRow {
  /** 2-12, a 2d6 SUM — not an 11-66 code. */
  total: number;
  /** The Pit Trouble Description cell, verbatim including its typos. */
  text: string;
  placement: PitTroublePlacement;
  /** Row 2 only — the crew is hurt and the race changes for this team. */
  crewInjury?: true;
}

/**
 * Appendix B2, all eleven rows, transcribed off the rendered EMF.
 *
 * Verbatim quirks preserved: "temporarly" (8), "pushe back in to pit" (9), and
 * the mismatched braces on 8 and 10.
 */
export const PIT_TROUBLE: readonly PitTroubleRow[] = [
  { total: 2, text: 'Pit crewman struck by another driver (See * below)', placement: 'last-in-current-group', crewInjury: true },
  { total: 3, text: 'Driver stalls car on exit has to be push started [LPCG]', placement: 'last-in-current-group' },
  { total: 4, text: 'Minor fender damage is rubbing on tire and has to be pried out [LPCG]', placement: 'last-in-current-group' },
  { total: 5, text: 'Fluid leak spotted and has to be inspected [LPCG]', placement: 'last-in-current-group' },
  { total: 6, text: 'Lug wrench malfunctions, replacement has to be retrieved [LPCG]', placement: 'last-in-current-group' },
  { total: 7, text: "Pit lane traffic delays driver's exit [LPCG]", placement: 'last-in-current-group' },
  { total: 8, text: 'Fuel canister temporarly sticks in fuel filler tube {LPCG]', placement: 'last-in-current-group' },
  { total: 9, text: 'Driver locks up brakes, has to be pushe back in to pit [LPCG]', placement: 'last-in-current-group' },
  { total: 10, text: 'Frozen Lug Nut [LPCG}', placement: 'last-in-current-group' },
  { total: 11, text: 'Pit Official sees missing lug nut (See ** below)', placement: 'last-on-lead-lap' },
  { total: 12, text: 'Spark causes a fire, must be extinguished & checked for damage (See *** below)', placement: 'fire' },
];

export const PIT_TROUBLE_BY_TOTAL: Readonly<Record<number, PitTroubleRow>> = Object.freeze(
  Object.fromEntries(PIT_TROUBLE.map((r) => [r.total, r])),
);

/** The three footnotes, verbatim. */
export const PIT_TROUBLE_NOTES = Object.freeze({
  '*': 'Pit crewman injured - All remaining Fast Pit checks in this race fail.',
  '**': 'Driver returns to field as the last driver on the lead lap.',
  '***':
    'Roll d6  1-5 Driver returns to field as last driver in the next lower group, if already in the ' +
    'bottom group driver returns as the last driver in the bottom down group. ' +
    'If result is a 6 - Car is forced to retire from the race due to Pit fire damage.',
});

// ---------------------------------------------------------------------------
// Readings that the chart does not print
// ---------------------------------------------------------------------------

/**
 * ⚠ READING — "Last Position in the Current Group" for the race LEADER.
 *
 * Only the LEADER, the TOP group and the MIDDLE group take Fast Pit rolls
 * (RB/xii: "the drivers in the BOTTOM group do not get the opportunity"), so a
 * pit trouble can land on the leader. The leader is a position, not a group, and
 * B2 says nothing about him.
 *
 * Read as: the leader's current group is the TOP group — the group he is racing
 * in — so he drops to the last position of TOP. The alternative, leaving him
 * where he is, would make a printed result do nothing at all for the one car it
 * costs the most.
 */
export const LEADER_GROUP_FOR_LPCG: Group = 'top';

/**
 * ✅ BUILT — "the bottom down group" on row 12 is the laps-down sub-group
 * (LDSG), and it exists as of `lapsDown.ts`. A bottom-group car that draws the
 * fire and rolls 1-5 now goes where the sheet says: the end of the LDSG, a lap
 * down.
 *
 * This constant stays rather than being deleted, because the laps-down rule is
 * optional and the degraded reading is still what happens when it is switched
 * off. `resolvePitTurn` reports the row as degraded in exactly that case.
 */
export const LDSG_EXISTS = true;
export const CITE_LDSG_DEGRADED =
  'EZRA-S2 Appendix B2 note *** sends a bottom-group car to "the bottom down group" (LDSG). The ' +
  'laps-down sub-group IS now built, so this row is applied as printed. It is still reported as ' +
  'degraded when the laps-down optional rule is switched OFF, because with one BOTTOM stack there ' +
  'is nowhere on the board that means "a lap down" and the end of the stack is the nearest thing.';

/**
 * ✅ EXACT — row 11, "last driver on the lead lap", both ways.
 *
 * With the laps-down rule ON, this is the slot directly above the LDSG marker:
 * a real, distinct place on the board. With it OFF, every running car is on the
 * lead lap by definition, so the last driver on the lead lap is the last car in
 * the field — the same answer the old reading gave, and exact for the same
 * reason it was exact then. This row was never degraded; it was only ever
 * *trivially* satisfied, and now it is satisfied properly.
 */

// ---------------------------------------------------------------------------
// The roll
// ---------------------------------------------------------------------------

export type PitTroubleOutcome =
  | 'last-in-current-group'
  | 'last-on-lead-lap'
  | 'next-lower-group'
  | 'retired';

export interface PitTroubleResult {
  /** 2-12. */
  total: number;
  row: PitTroubleRow;
  driverId: string;
  /** The group the car was in when the trouble happened. */
  fromGroup: Group;
  outcome: PitTroubleOutcome;
  /** Row 12 only — the d6 from note ***. */
  fireRoll: number | null;
  /** Row 2 only — every remaining Fast Pit check for this car fails. */
  crewInjured: boolean;
  /** True when a printed placement had to be degraded because LDSG is absent. */
  degraded: boolean;
  description: string;
  ruleCitation: string;
  dice: { pair: [number, number]; total: number; fire: number | null };
}

/**
 * Roll on Appendix B2. `fromGroup` is where the car is at the moment of the
 * pit stop; `leader` is folded into `top` for placement per
 * `LEADER_GROUP_FOR_LPCG`.
 */
export function rollPitTrouble(
  dice: Dice,
  driverId: string,
  fromGroup: Group,
  /**
   * Is the laps-down sub-group switched on for this race? Defaults to whether
   * it is BUILT, which is the honest default now that it is. Pass `false` and
   * row 12's bottom-group branch degrades again, exactly as it did before —
   * the rule is optional, so the degraded reading has to stay reachable.
   */
  ldsg: boolean = LDSG_EXISTS,
): PitTroubleResult {
  const roll = dice.sum2d6();
  const row = PIT_TROUBLE_BY_TOTAL[roll.total];
  if (!row) throw new RangeError(`pit trouble: no Appendix B2 row for 2d6 total ${roll.total}`);

  let outcome: PitTroubleOutcome;
  let fireRoll: number | null = null;
  let degraded = false;

  if (row.placement === 'fire') {
    fireRoll = dice.d6();
    if (fireRoll === 6) {
      outcome = 'retired';
    } else {
      outcome = 'next-lower-group';
      // "if already in the bottom group driver returns as the last driver in
      // the bottom down group" — and the bottom down group does not exist yet.
      if (fromGroup === 'bottom' && !ldsg) degraded = true;
    }
  } else if (row.placement === 'last-on-lead-lap') {
    outcome = 'last-on-lead-lap';
  } else {
    outcome = 'last-in-current-group';
  }

  const citations = [CITE_PIT_TROUBLE, CITE_PIT_TROUBLE_TRIGGER];
  if (row.placement === 'last-in-current-group') citations.push(CITE_LPCG);
  if (degraded) citations.push(CITE_LDSG_DEGRADED);

  return {
    total: roll.total,
    row,
    driverId,
    fromGroup,
    outcome,
    fireRoll,
    crewInjured: row.crewInjury === true,
    degraded,
    ruleCitation: citations.join('; '),
    dice: { pair: roll.dice, total: roll.total, fire: fireRoll },
    description: describePitTrouble(row, outcome, fireRoll, degraded, driverId),
  };
}

export function describePitTrouble(
  row: PitTroubleRow,
  outcome: PitTroubleOutcome,
  fireRoll: number | null,
  degraded: boolean,
  who: string,
): string {
  const head = `Trouble in the pits for ${who} — ${row.text.replace(/\s*[[{]LPCG[\]}]/, '').replace(/\s*\(See \**\s*below\)/, '')}.`;
  switch (outcome) {
    case 'last-in-current-group':
      return `${head} He goes back out last in his group.` +
        (row.crewInjury ? ' The crewman is hurt — every remaining Fast Pit check for this team fails tonight.' : '');
    case 'last-on-lead-lap':
      return `${head} The official spots it and he returns as the last car on the lead lap.`;
    case 'next-lower-group':
      return `${head} Fire out, damage checked (d6 ${fireRoll}) — he returns last in the next group down` +
        (degraded ? ', which with no laps-down group modelled is the end of the BOTTOM group.' : '.');
    case 'retired':
      return `${head} The d6 comes up 6 — the fire damage is too much and the car is out of the race.`;
  }
}

/** How many rows carry each placement — used by the tests to pin the transcription. */
export function placementCounts(): Record<PitTroublePlacement, number> {
  const counts: Record<PitTroublePlacement, number> = {
    'last-in-current-group': 0,
    'last-on-lead-lap': 0,
    fire: 0,
  };
  for (const row of PIT_TROUBLE) counts[row.placement]++;
  return counts;
}
